// Cargador de map1 en runtime.
//
// map1.json pesa ~35 MB. Importarlo estático (`import map1Json from ...`) lo hornea dentro del
// bundle JS (main-*.js), obligando al navegador a bajar+parsear 35 MB antes de mostrar el login.
// En vez de eso, `?url` le dice a Vite que emita el JSON como un asset aparte (hasheado, cacheable)
// y nos devuelve su URL; acá lo fetcheamos UNA sola vez (memoizado) fuera del bundle.
//
// En PROD el asset se sirve GZIPPEADO (`<hash>.json.gz`, ~4 MB) porque Cloudflare Pages no acepta
// archivos > 25 MiB (el prepDist.mjs lo gzippea y borra el `.json` original). Acá descomprimimos con
// el `DecompressionStream` nativo del navegador. En DEV el `.gz` no existe → fetch del `.json` crudo.
// Ver docs/PERFORMANCE_OPTIMIZATION.md (Target #1) y project_map_bundle_todo.
import mapUrl from '@shared/maps/map1.json?url';
import { generateBorderTiles } from '@shared/maps/mapUtils';
import type { MapData } from '@shared/types/map';

let cached: MapData | null = null;
let inflight: Promise<MapData> | null = null;
let borderedCache: MapData | null = null;

/**
 * Fetch del mapa en PROD: baja el `.gz` y lo descomprime. Robusto ante el comportamiento de
 * Cloudflare: si por algún motivo el servidor ya aplicó `Content-Encoding: gzip` (el fetch llega
 * ya descomprimido), lo detectamos por los magic bytes (1f 8b) y parseamos directo.
 */
async function fetchMap1Prod(): Promise<MapData> {
  const resp = await fetch(mapUrl + '.gz');
  if (!resp.ok) throw new Error(`map1 fetch (.gz) falló: ${resp.status}`);
  const buf = await resp.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const isGzip = bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (isGzip && typeof DecompressionStream !== 'undefined') {
    const stream = new Response(buf).body!.pipeThrough(new DecompressionStream('gzip'));
    return (await new Response(stream).json()) as MapData;
  }
  // Ya venía descomprimido (Content-Encoding del server) o sin soporte de DecompressionStream.
  return JSON.parse(new TextDecoder().decode(buf)) as MapData;
}

/** Fetchea map1.json una sola vez (memoizado) y cachea el crudo. */
export function loadMap1Raw(): Promise<MapData> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    const load = import.meta.env.PROD
      ? fetchMap1Prod()
      : fetch(mapUrl).then((r) => r.json() as Promise<MapData>);
    inflight = load.then((d: MapData) => {
      cached = d;
      return d;
    });
  }
  return inflight;
}

/** Getter síncrono: devuelve el crudo ya cargado; tira si no se llamó loadMap1Raw() antes. */
export function getMap1Raw(): MapData {
  if (!cached) {
    throw new Error('map1 no cargado — llamar (y esperar) loadMap1Raw() primero');
  }
  return cached;
}

/**
 * map1 CON la capa de borde, memoizado y SIN deep-clone.
 *
 * `loadMapWithBorders` (shared) hace `JSON.parse(JSON.stringify(raw))` — un deep-clone de los ~35MB
 * de tiles — sólo para poder appendear la capa de borde sin mutar el original. Eso costaba ~300ms de
 * freeze del hilo principal, y se hacía 3 VECES durante el login (fondo de auth + preload + create),
 * congelando el botón "Entrar" y las animaciones del fondo (AmbientCrowd).
 *
 * Acá lo hacemos UNA vez con una copia **shallow**: appendear la capa de borde sólo necesita un
 * `layers` nuevo (array shallow); los tiles se comparten por referencia (el runtime del juego trata
 * el mapa como read-only; el único mutador, MapEditorScene, clona su propio mapa aparte). Resultado:
 * de ~900ms de deep-clones a ~0. Ver docs/PERFORMANCE_OPTIMIZATION.md.
 */
export function getMap1Bordered(): MapData {
  if (borderedCache) return borderedCache;
  const raw = getMap1Raw();
  const borderLayer = { name: '_border', z: -1, tiles: generateBorderTiles(raw.bounds) };
  borderedCache = { ...raw, layers: [...raw.layers, borderLayer] };
  return borderedCache;
}
